import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from '_common/hooks';

import {
  ThunksTable,
  TableColumn,
  TableRow,
  onDoubleClickHandler,
  Cell,
} from '_common/components/Table';

import { useMemo } from 'react';
import { EmptyState } from '@ets/design-system';

import { navigateToObject } from 'router/navigation';

import { openAndUpdateModal } from '_common/modals/ModalsSlice';

import Footer from '_common/components/Table/Footer/Footer';
import SelectionCounter from '_common/components/Table/Footer/SelectionCounter';

import { completeAction } from 'App/redux/onboardingSlice';

type TableProps = {
  fetchObjects: (listParams: Request.AllListParams) => Promise<Request.FetchResponse>;
};

const Table = ({ fetchObjects }: TableProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const data = useSelector((state) => state.app.data);
  const list = useSelector((state) => state.table.identity.spaces.list);
  const loading = useSelector((state) => state.app.loading.isOpen);

  const handleOpenDeleteSpaceModal = (object: Objekt) => {
    dispatch(
      openAndUpdateModal({
        modal: 'ConfirmationModal',
        data: {
          title: 'DELETE_ITEM_QUANTITY',
          titleValues: { total: 1 },
          message: 'DELETING_THE_SELECTED_ITEM_QUANTITY_WILL_PERMANENTLY_REMOVE_CONFIRM',
          messageValues: { total: 1 },
          confirmButtonTextId: 'global.delete',
          confirmButtonType: 'danger',
          cancelButtonTextId: 'global.cancel',
          actionCode: 'deleteObjects',
          actionValue: { objectList: [object], identity: 'spaces' },
          cancelButtonShow: true,
        },
      }),
    );
  };

  const columns = useMemo<TableColumn<Objekt>[]>(() => {
    return [
      {
        id: 'name',
        accessorKey: 'name',
        minWidth: 296,
        header: intl.formatMessage({ id: 'storage.browserHeader.name' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-name-column`}>
              <Cell.ObjectName object={object} testId={`spaces-${object.id}-object-name`} />
            </Cell>
          );
        },
      },
      {
        id: 'time.modification',
        accessorKey: 'time.modification',
        size: 192,
        header: intl.formatMessage({ id: 'storage.browserHeader.dateModified' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-date-modified-column`}>
              <Cell.DateTime
                date={{ date: object.time.modification }}
                time={{ time: object.time.modification }}
              />
            </Cell>
          );
        },
      },
      {
        id: 'shared_with',
        accessorKey: 'shared_with',
        size: 160,
        enableSorting: false,
        header: intl.formatMessage({ id: 'storage.browserHeader.sharedWith' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-shared-column`}>
              <Cell.Collaborators object={object} personalLabel />
            </Cell>
          );
        },
      },
    ];
  }, []);

  const rowsData = useMemo(() => {
    return list?.map<TableRow<Objekt>>((objectId) => {
      const object = data[objectId];
      return {
        ...object,
        suffix: object.personal
          ? undefined
          : {
              actions: [
                {
                  label: intl.formatMessage({ id: 'spaces.deleteSpace' }),
                  onClick: () => {
                    handleOpenDeleteSpaceModal(object);
                  },
                },
              ],
            },
      };
    });
  }, [list, data]);

  const handleRowDoubleClick: onDoubleClickHandler = (row) => {
    const object = data[(row as Objekt).id];
    navigateToObject('space', object?.id);
    dispatch(completeAction('explorer_open_space'));
  };

  const renderEmptyView = () => {
    if (!loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
          <EmptyState
            size="l"
            icon="box-archive"
            title={intl.formatMessage({ id: 'storage.browserHeader.noSpaces' })}
            description=""
            testId="spaces-empty-list"
          />
        </div>
      );
    }
    return null;
  };

  return (
    <ThunksTable
      identity={'spaces'}
      fetchObjects={fetchObjects}
      options={{ columns, data: rowsData }}
      lazyLoading
      loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
      onDoubleClick={handleRowDoubleClick}
      renderEmptyState={renderEmptyView}
      renderFooter={() => (
        <Footer>
          <SelectionCounter identity={'spaces'} />
        </Footer>
      )}
      testId="spaces"
    />
  );
};

export default Table;
